// DESCRIPTION: Root CalendarApp component — owns state, fetches events, renders the active view.

import React, { useState, useEffect, useCallback } from 'react';
import apiFetch from '@wordpress/api-fetch';
import CalendarHeader from './CalendarHeader';
import CalendarFilters from './CalendarFilters';
import MonthView from './MonthView';
import WeekView from './WeekView';
import DayView from './DayView';
import ListView from './ListView';

const config = window.nemtlyCalendar || {};
const strings = config.strings || {};

/**
 * Derive the ISO date string (YYYY-MM-DD) for a given Date object.
 *
 * @param {Date} date
 * @return {string}
 */
function toDateString( date ) {
	return date.toISOString().slice( 0, 10 );
}

/**
 * Given a view type and anchor date, return the { dateFrom, dateTo } window
 * that must be fetched from the API.
 *
 * For month view we include padding days so the grid is full (always 6 rows).
 *
 * @param {string} view       month|week|day|list
 * @param {Date}   anchorDate Anchor date (first of month for month view, etc.)
 * @param {number} firstDay   0 = Sunday, 1 = Monday
 * @return {{ dateFrom: string, dateTo: string }}
 */
function getDateWindow( view, anchorDate, firstDay ) {
	const d = new Date( anchorDate );

	if ( view === 'month' ) {
		// First visible day: start of the week that contains the 1st of the month.
		const firstOfMonth = new Date( d.getFullYear(), d.getMonth(), 1 );
		const dayOfWeek = firstOfMonth.getDay(); // 0=Sun…6=Sat
		const offset = ( dayOfWeek - firstDay + 7 ) % 7;
		const gridStart = new Date( firstOfMonth );
		gridStart.setDate( gridStart.getDate() - offset );

		// Always show 6 rows (42 days).
		const gridEnd = new Date( gridStart );
		gridEnd.setDate( gridEnd.getDate() + 41 );

		return {
			dateFrom: toDateString( gridStart ),
			dateTo: toDateString( gridEnd ),
		};
	}

	if ( view === 'week' ) {
		const dayOfWeek = d.getDay();
		const offset = ( dayOfWeek - firstDay + 7 ) % 7;
		const weekStart = new Date( d );
		weekStart.setDate( weekStart.getDate() - offset );
		const weekEnd = new Date( weekStart );
		weekEnd.setDate( weekEnd.getDate() + 6 );
		return {
			dateFrom: toDateString( weekStart ),
			dateTo: toDateString( weekEnd ),
		};
	}

	if ( view === 'day' ) {
		const day = toDateString( d );
		return { dateFrom: day, dateTo: day };
	}

	// list — show next 30 days from anchor
	const listEnd = new Date( d );
	listEnd.setDate( listEnd.getDate() + 29 );
	return { dateFrom: toDateString( d ), dateTo: toDateString( listEnd ) };
}

/**
 * CalendarApp
 *
 * @param {Object}  props
 * @param {string}  props.initialView       Overrides the global default view.
 * @param {string}  props.initialCategories Comma-separated category IDs.
 * @param {string}  props.initialTags       Comma-separated tag IDs.
 * @param {boolean} props.showFilters       Whether to render the filter bar.
 * @param {string}  props.availableViews    Comma-separated list of enabled views (overrides global).
 * @param {string}  props.eventType         Filter events by type slug.
 * @param {boolean} props.showToolbar       Whether to show the calendar header/toolbar.
 * @param {string}  props.weekStartsOn      Override first day of week (0=Sun, 1=Mon).
 */
export default function CalendarApp( {
	initialView,
	initialCategories,
	initialTags,
	showFilters,
	availableViews: availableViewsProp,
	eventType,
	showToolbar,
	weekStartsOn,
	eventsPerCell: eventsPerCellProp,
} ) {
	const defaultView = initialView || config.defaultView || 'month';
	const firstDay =
		weekStartsOn !== '' && weekStartsOn !== undefined
			? parseInt( weekStartsOn, 10 )
			: config.firstDayOfWeek || 0;

	// Resolve which views are available — prop override > global config > all views.
	const resolvedViews = availableViewsProp
		? availableViewsProp.split( ',' ).filter( Boolean )
		: Array.isArray( config.availableViews ) && config.availableViews.length
		? config.availableViews
		: [ 'month', 'week', 'day', 'list' ];

	const parseIds = ( str ) =>
		str ? str.split( ',' ).map( Number ).filter( Boolean ) : [];

	const effectiveEventsPerCell = eventsPerCellProp
		? parseInt( eventsPerCellProp, 10 )
		: config.eventsPerCell || 3;

	const [ view, setView ] = useState( defaultView );
	const [ anchor, setAnchor ] = useState( () => {
		const now = new Date();
		return new Date( now.getFullYear(), now.getMonth(), 1 );
	} );
	const [ filters, setFilters ] = useState( {
		categories: parseIds( initialCategories ),
		tags: parseIds( initialTags ),
	} );
	const [ events, setEvents ] = useState( [] );
	const [ useLocalTz, setUseLocalTz ] = useState( false );
	const [ loading, setLoading ] = useState( true );
	const [ error, setError ] = useState( null );

	const fetchEvents = useCallback( async () => {
		setLoading( true );
		setError( null );

		const { dateFrom, dateTo } = getDateWindow( view, anchor, firstDay );

		const params = new URLSearchParams( {
			date_from: dateFrom,
			date_to: dateTo,
		} );
		filters.categories.forEach( ( id ) =>
			params.append( 'categories[]', id )
		);
		filters.tags.forEach( ( id ) => params.append( 'tags[]', id ) );
		if ( eventType ) {
			params.append( 'event_type', eventType );
		}

		try {
			const response = await apiFetch( {
				path: `/nemtly-booking/v1/events/calendar?${ params.toString() }`,
			} );
			const fetched = response.data || [];
			setEvents(
				config.showPastEvents
					? fetched
					: fetched.filter( ( event ) => ! event.is_past )
			);
		} catch ( err ) {
			setError(
				strings.error || 'Could not load events. Please try again.'
			);
		} finally {
			setLoading( false );
		}
	}, [ view, anchor, filters, firstDay, eventType ] );

	useEffect( () => {
		fetchEvents();
	}, [ fetchEvents ] );

	// Navigation helpers.
	const navigate = ( direction ) => {
		setAnchor( ( prev ) => {
			const d = new Date( prev );
			if ( view === 'month' ) {
				d.setMonth( d.getMonth() + direction );
				return new Date( d.getFullYear(), d.getMonth(), 1 );
			}
			if ( view === 'week' ) {
				d.setDate( d.getDate() + direction * 7 );
				return d;
			}
			if ( view === 'day' ) {
				d.setDate( d.getDate() + direction );
				return d;
			}
			// list
			d.setDate( d.getDate() + direction * 30 );
			return d;
		} );
	};

	const goToToday = () => {
		const now = new Date();
		if ( view === 'month' ) {
			setAnchor( new Date( now.getFullYear(), now.getMonth(), 1 ) );
		} else {
			setAnchor(
				new Date( now.getFullYear(), now.getMonth(), now.getDate() )
			);
		}
	};

	const { dateFrom, dateTo } = getDateWindow( view, anchor, firstDay );

	// Calendar height style from settings.
	const calendarStyle = {};
	if ( config.calendarHeight === 'fixed' && config.calendarHeightPx ) {
		calendarStyle.height = `${ config.calendarHeightPx }px`;
		calendarStyle.overflow = 'auto';
	}

	return (
		<div className="nb-event-calendar-app" style={ calendarStyle }>
			{ showToolbar !== false && (
				<CalendarHeader
					view={ view }
					anchor={ anchor }
					dateFrom={ dateFrom }
					dateTo={ dateTo }
					availableViews={ resolvedViews }
					useLocalTz={ useLocalTz }
					onToggleLocalTz={ () =>
						setUseLocalTz( ( prev ) => ! prev )
					}
					onPrev={ () => navigate( -1 ) }
					onNext={ () => navigate( 1 ) }
					onToday={ goToToday }
					onViewChange={ ( v ) => {
						setView( v );
						// When switching to a non-month view, anchor on today if currently on month start.
						const now = new Date();
						if ( v !== 'month' && anchor.getDate() === 1 ) {
							setAnchor(
								new Date(
									now.getFullYear(),
									now.getMonth(),
									now.getDate()
								)
							);
						}
					} }
				/>
			) }

			{ showFilters && (
				<CalendarFilters
					filters={ filters }
					onFiltersChange={ setFilters }
				/>
			) }

			{ loading && (
				<div
					className="nb-calendar-skeleton nb-grid nb-gap-2"
					style={ { gridTemplateColumns: 'repeat(7,1fr)' } }
				>
					{ Array.from( { length: 21 } ).map( ( _, i ) => (
						<div key={ i } className="nb-calendar-skeleton-cell" />
					) ) }
				</div>
			) }

			{ ! loading && error && (
				<div className="nb-calendar-error">
					<p>{ error }</p>
					<button
						className="nb-calendar-retry-btn"
						onClick={ fetchEvents }
					>
						{ strings.retry || 'Retry' }
					</button>
				</div>
			) }

			{ ! loading && ! error && view === 'month' && (
				<MonthView
					events={ events }
					anchor={ anchor }
					firstDay={ firstDay }
					eventsPerCell={ effectiveEventsPerCell }
					dateFrom={ dateFrom }
					useLocalTz={ useLocalTz }
					onDayClick={ ( date ) => {
						setAnchor( date );
						setView( 'day' );
					} }
				/>
			) }

			{ ! loading && ! error && view === 'week' && (
				<WeekView
					events={ events }
					anchor={ anchor }
					dateFrom={ dateFrom }
					useLocalTz={ useLocalTz }
				/>
			) }

			{ ! loading && ! error && view === 'day' && (
				<DayView
					events={ events }
					anchor={ anchor }
					useLocalTz={ useLocalTz }
				/>
			) }

			{ ! loading && ! error && view === 'list' && (
				<ListView
					events={ events }
					dateFrom={ dateFrom }
					dateTo={ dateTo }
					useLocalTz={ useLocalTz }
				/>
			) }
		</div>
	);
}
